import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  closeCashRegisterService,
  createCashRegisterService,
  getCashRegisterOpen,
} from '@/services/cash-register';
import { CashRegister } from '@modules/cash-register/types';
import { CashRegisterContextType, CashRegisterStore } from '@/store/cash-register';
import { errorCashRegister, warningCashRegister } from '@/utils';
import { useSnackbar } from '@commons/snackbar';
import { useCallback, useEffect, useMemo } from 'react';
import { useAuthContext } from '@hooks/useAuthContext';
import { useShallow } from 'zustand/react/shallow';

export const useCashRegisterContext = (): Omit<
  CashRegisterContextType,
  'initial' | 'create' | 'activateLoading'
> => {
  const context = CashRegisterStore(useShallow((state) => state));
  const queryClient = useQueryClient();
  const queryKey = ['cash-register'];
  const { enqueueSnackbar } = useSnackbar();
  const { user } = useAuthContext();

  if (!context)
    throw new Error('useCashRegisterContext context must be use inside CashRegisterProvider');
  const generateError = (value: any) => {
    const errorTemp = Array.isArray(value?.details) ? value?.details[0] : value;
    const errorMessage = errorCashRegister[errorTemp?.message];
    const warningMessage = warningCashRegister[errorTemp?.message];
    context.initial();
    enqueueSnackbar(errorMessage ?? warningMessage ?? errorTemp?.message, {
      variant: warningMessage ? 'warning' : 'error',
    });
  };
  const { mutateAsync: createCashRegisterFn } = useMutation({
    mutationKey: ['cash-register'],
    mutationFn: createCashRegisterService,
    onMutate: async () => {
      context.activateLoading();
      await queryClient.cancelQueries({ queryKey });
    },
    onSuccess: (data: CashRegister) => {
      context.create(data);
      queryClient.invalidateQueries({ queryKey }).then((_r) => null);
      enqueueSnackbar('Open Cash Register!');
    },
    onError: generateError,
  });
  const { mutateAsync: closeCashRegisterFn } = useMutation({
    mutationFn: closeCashRegisterService(context?.box?.id),
    onSuccess: (value: any) => {
      if (!errorCashRegister[value?.message]) {
        context.initial();
        queryClient.invalidateQueries({ queryKey }).then((_r) => null);
        enqueueSnackbar('Close Cash Register!');
      }
    },
    onError: (value: any) => {
      generateError(value);
    },
    retry: 1,
  });
  const { data, refetch } = useQuery({
    queryKey,
    queryFn: getCashRegisterOpen,
    retry: 1,
    enabled: !!user?.uid,
  });
  useEffect(() => {
    if (data) {
      context.create(data); // Actualización del store
    }
  }, [data, context]); // Dependencias
  const createCashRegister = useCallback(
    async (values: unknown) => createCashRegisterFn(values),
    [createCashRegisterFn]
  );

  const closeCashRegister = useCallback(
    async (values: Record<string, unknown>) =>
      closeCashRegisterFn({ userId: user?.uid, ...values }),
    [closeCashRegisterFn, user?.uid]
  );

  return useMemo(
    () => ({
      loading: context.loading,
      isOpen: context.isOpen,
      box: context.box,
      createCashRegister,
      refetch,
      closeCashRegister,
    }),
    [context.box, context.isOpen, createCashRegister, closeCashRegister, refetch, context.loading]
  );
};
